import { useState } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { api } from '@/lib/api';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const INTERESTS = ['Private Demo', 'Strategic Consultation', 'Architecture Session', 'Executive Workforce Design',
  'Sales & Operations Agents', 'Legal / Compliance Agents', 'Research / Intelligence Agents',
  'Private Office Agents', 'Enterprise Multi-Team Deployment', 'Multi-Entity Workforce Design', 'Other'];
const COMPANY_TYPES = ['Family Office', 'Holding Company', 'Private Equity', 'Law Firm', 'Healthcare Group',
  'Financial Institution', 'Real Estate Group', 'Luxury Brand', 'Trading Company', 'Enterprise Operator', 'Executive Office', 'Other'];
const TEAM_SIZES = ['1-10', '11-25', '26-50', '51-100', '101-250', '251-500', '501-1000', '1000+'];
const URGENCIES = ['Exploring', 'Near-term', 'Active planning', 'Immediate discussion'];
const CONTACTS = ['Email', 'Phone', 'WhatsApp', 'Digital Meeting'];

const INIT = { full_name: '', company_name: '', email: '', phone: '', country: '', company_type: '',
  team_size: '', main_interest: '', message: '', job_title: '', urgency: '', preferred_contact: '',
  num_entities: '', consent_contact: false, honeypot: '' };

export default function BookingContact() {
  const [tab, setTab] = useState('form');
  const [form, setForm] = useState(INIT);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return;
    setLoading(true);
    try {
      await api.createLead({ ...form, source: 'contact_form', source_section: 'booking_contact' });
      setStatus('success');
      setForm(INIT);
    } catch (err) {
      setStatus(err.response?.status === 429 ? 'rate_limit' : 'error');
    }
    setLoading(false);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <section id="contact" className="relative py-24 sm:py-32" data-testid="booking-contact-section">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-aurocle-gold/5 rounded-full blur-[200px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-12">
            <div className="overline mb-4">Get Started</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
              Begin the conversation.
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">
              Schedule a private digital meeting with Aurocle and explore how a structured multi-agent workforce could be designed around your business.
            </p>
          </div>
        </ScrollReveal>

        <ScrollReveal delay={100}>
          <div className="flex justify-center gap-4 mb-10">
            <button onClick={() => setTab('form')} data-testid="tab-inquiry"
              className={`px-6 py-2.5 text-xs font-mono uppercase tracking-wider transition-all border ${
                tab === 'form' ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/8 text-aurocle-text-secondary hover:text-aurocle-text'}`}>
              Send Inquiry
            </button>
            <button onClick={() => setTab('book')} data-testid="tab-booking"
              className={`px-6 py-2.5 text-xs font-mono uppercase tracking-wider transition-all border ${
                tab === 'book' ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold' : 'border-white/8 text-aurocle-text-secondary hover:text-aurocle-text'}`}>
              Book Digital Meeting
            </button>
          </div>
        </ScrollReveal>

        {tab === 'form' && (
          <ScrollReveal delay={150}>
            {status === 'success' ? (
              <div className="max-w-2xl mx-auto text-center py-16 glass-card p-12" data-testid="form-success">
                <CheckCircle size={48} className="text-aurocle-gold mx-auto mb-6" />
                <h3 className="font-heading text-2xl text-aurocle-text mb-3">Your request has been received.</h3>
                <p className="text-sm text-aurocle-platinum-muted mb-4">We will review your inquiry and respond with appropriate next steps.</p>
                <p className="text-xs text-aurocle-text-secondary mb-8">Your inquiry has been forwarded to <a href="mailto:info@aurocle.world" className="text-aurocle-gold hover:underline">info@aurocle.world</a></p>
                <div className="flex items-center justify-center gap-4">
                  <a href="mailto:info@aurocle.world?subject=Inquiry%20-%20Aurocle%20Agents" className="btn-gold text-xs" data-testid="success-email-link">
                    Email info@aurocle.world
                  </a>
                  <button onClick={() => setStatus(null)} className="btn-outline text-xs">Submit Another Inquiry</button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="max-w-4xl mx-auto glass-card p-8 lg:p-12" data-testid="contact-form">
                <input type="text" name="honeypot" value={form.honeypot} onChange={set('honeypot')} className="hidden" tabIndex={-1} autoComplete="off" />

                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 mb-8">
                  <div><label className="premium-label">Full Name *</label><input type="text" required value={form.full_name} onChange={set('full_name')} className="premium-input" data-testid="input-full-name" placeholder="Your full name" /></div>
                  <div><label className="premium-label">Company Name</label><input type="text" value={form.company_name} onChange={set('company_name')} className="premium-input" data-testid="input-company-name" placeholder="Your company" /></div>
                  <div><label className="premium-label">Work Email *</label><input type="email" required value={form.email} onChange={set('email')} className="premium-input" data-testid="input-email" placeholder="you@company.com" /></div>
                  <div><label className="premium-label">Phone / WhatsApp</label><input type="tel" value={form.phone} onChange={set('phone')} className="premium-input" data-testid="input-phone" placeholder="+31 ..." /></div>
                  <div><label className="premium-label">Country</label><input type="text" value={form.country} onChange={set('country')} className="premium-input" data-testid="input-country" placeholder="Country" /></div>
                  <div><label className="premium-label">Job Title</label><input type="text" value={form.job_title} onChange={set('job_title')} className="premium-input" data-testid="input-job-title" placeholder="CEO, Partner, Director..." /></div>
                  <div>
                    <label className="premium-label">Company Type</label>
                    <select value={form.company_type} onChange={set('company_type')} className="premium-select" data-testid="select-company-type">
                      <option value="">Select type</option>
                      {COMPANY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="premium-label">Team Size</label>
                    <select value={form.team_size} onChange={set('team_size')} className="premium-select" data-testid="select-team-size">
                      <option value="">Select size</option>
                      {TEAM_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="premium-label">Main Interest</label>
                    <select value={form.main_interest} onChange={set('main_interest')} className="premium-select" data-testid="select-interest">
                      <option value="">Select interest</option>
                      {INTERESTS.map(i => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="premium-label">Urgency</label>
                    <select value={form.urgency} onChange={set('urgency')} className="premium-select" data-testid="select-urgency">
                      <option value="">Select urgency</option>
                      {URGENCIES.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="premium-label">Number of Entities / Offices</label>
                    <input type="text" value={form.num_entities} onChange={set('num_entities')} className="premium-input" data-testid="input-entities" placeholder="e.g. 3" />
                  </div>
                  <div>
                    <label className="premium-label">Preferred Contact</label>
                    <select value={form.preferred_contact} onChange={set('preferred_contact')} className="premium-select" data-testid="select-contact-method">
                      <option value="">Select method</option>
                      {CONTACTS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div className="mb-8">
                  <label className="premium-label">Message</label>
                  <textarea value={form.message} onChange={set('message')} className="premium-textarea" data-testid="textarea-message" placeholder="Tell us about your business, challenges, and what you are looking to achieve..." />
                </div>

                <div className="flex items-start gap-3 mb-8">
                  <input type="checkbox" checked={form.consent_contact} onChange={(e) => setForm({...form, consent_contact: e.target.checked})}
                    className="mt-1 accent-aurocle-gold" data-testid="checkbox-consent" />
                  <span className="text-xs text-aurocle-text-secondary">I agree to be contacted regarding my inquiry and understand that my data will be handled in accordance with Aurocle's privacy policy.</span>
                </div>

                {status === 'error' && (
                  <div className="flex items-center gap-2 text-red-400 text-sm mb-4" data-testid="form-error">
                    <AlertCircle size={16} /> An error occurred. Please try again.
                  </div>
                )}
                {status === 'rate_limit' && (
                  <div className="flex items-center gap-2 text-amber-400 text-sm mb-4">
                    <AlertCircle size={16} /> Please wait a moment before submitting again.
                  </div>
                )}

                <button type="submit" disabled={loading} className="btn-gold w-full sm:w-auto" data-testid="submit-contact-form">
                  <Send size={14} /> {loading ? 'Submitting...' : 'Submit Inquiry'}
                </button>
              </form>
            )}
          </ScrollReveal>
        )}

        {tab === 'book' && (
          <ScrollReveal delay={150}>
            <div className="max-w-4xl mx-auto glass-card overflow-hidden" data-testid="calendly-embed-container">
              <div className="p-6 border-b border-white/5">
                <h3 className="font-heading text-lg text-aurocle-text mb-1">Schedule a Private Digital Meeting</h3>
                <p className="text-xs text-aurocle-platinum-muted">Select a time that works for you. All meetings are private and confidential.</p>
              </div>
              <div className="bg-aurocle-surface" style={{minHeight: '700px'}}>
                <iframe
                  src="https://calendly.com/aurocle/meet?background_color=0f0f11&text_color=ededed&primary_color=d4af37"
                  width="100%" height="700" frameBorder="0" title="Book a meeting with Aurocle"
                  data-testid="calendly-iframe"
                  style={{border: 'none', background: '#0F0F11'}}
                />
              </div>
            </div>
          </ScrollReveal>
        )}

        <ScrollReveal delay={300}>
          <div className="text-center mt-12">
            <p className="text-sm text-aurocle-platinum-muted mb-2">Prefer to speak directly?</p>
            <a href="mailto:info@aurocle.world" className="text-sm text-aurocle-gold hover:text-aurocle-gold-hover transition-colors" data-testid="contact-email-link">
              info@aurocle.world
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
